/**
 * Product Recommender Engine
 * Shared logic for all lead generation quiz tools
 */

window.ProductRecommender = (function() {
  'use strict';

  // ========== CONSTANTS ==========

  // Time constants (in milliseconds)
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

  // Analytics event names (prevents typos)
  const EVENTS = {
    EMAIL_CAPTURED: 'email_captured',
    QUIZ_STARTED: 'quiz_started',
    QUIZ_COMPLETED: 'quiz_completed',
    PRODUCT_ADDED: 'product_added_from_quiz'
  };

  // Fallback discount code
  const FALLBACK_DISCOUNT_CODE = 'FIRST10';

  let config = null;
  let initPromise = null;

  /**
   * Initialize the recommender with data
   * Uses Promise cache to prevent race conditions
   */
  async function init() {
    // Return cached config if already loaded
    if (config) return config;

    // Return existing promise if init is already in progress
    if (initPromise) return initPromise;

    // Start loading and cache the promise
    initPromise = (async () => {
      try {
        // Check sessionStorage cache first (persists during session)
        const cached = sessionStorage.getItem('recommender_data');
        if (cached) {
          config = JSON.parse(cached);
          return config;
        }

        const dataUrl = window.RECOMMENDER_DATA_URL || '/assets/product-recommender-data.json';
        const response = await fetch(dataUrl, {
          cache: 'force-cache' // Use browser cache when available
        });
        if (!response.ok) {
          throw new Error(`Failed to load data: ${response.status}`);
        }
        config = await response.json();

        // Cache in sessionStorage for faster subsequent loads
        try {
          sessionStorage.setItem('recommender_data', JSON.stringify(config));
        } catch (e) {
          // Ignore quota errors
        }

        return config;
      } catch (error) {
        console.error('[ProductRecommender] Failed to load recommender data:', error);
        initPromise = null; // Allow retry on failure
        return null;
      }
    })();

    return initPromise;
  }

  /**
   * Get recommendations based on selected symptoms
   *
   * SCORING ALGORITHM:
   * ==================
   * Products are scored based on how well they match selected symptoms.
   * Each symptom has "primary" products (strong match) and "secondary"
   * products (helpful but not the main solution).
   *
   * Scoring weights (from product-recommender-data.json):
   * - Primary match: +10 points
   * - Secondary match: +5 points
   *
   * Products accumulate points across all selected symptoms, allowing
   * products that address multiple issues to bubble to the top.
   *
   * Example: User selects "Itching" + "Digestive Issues"
   * - JOLLY GUT: 10 (primary for digestive) + 5 (secondary for itching) = 15
   * - NO RUFF: 10 (primary for itching) = 10
   * - Result: JOLLY GUT ranks higher because it helps with both
   *
   * @param {string[]} selectedSymptoms - Array of symptom IDs (e.g., ['itching', 'joint-pain'])
   * @returns {Object[]} Top 3 products sorted by score descending
   */
  function getSymptomRecommendations(selectedSymptoms) {
    if (!config) return [];

    // Accumulator for product scores: { productId: totalScore }
    const scores = {};
    const { symptoms, products, scoring } = config;

    // STEP 1: Calculate scores for each selected symptom
    selectedSymptoms.forEach(symptomId => {
      const symptom = symptoms[symptomId];
      if (!symptom) return;

      // Primary products get full weight (10 points)
      // These are the "go-to" products for this symptom
      symptom.primary.forEach(productId => {
        scores[productId] = (scores[productId] || 0) + scoring.primaryWeight;
      });

      // Secondary products get partial weight (5 points)
      // These complement the primary but aren't the main solution
      symptom.secondary.forEach(productId => {
        scores[productId] = (scores[productId] || 0) + scoring.secondaryWeight;
      });
    });

    // STEP 2: Convert scores object to sorted array
    // Sort by score descending so highest-scoring products come first
    const recommendations = Object.entries(scores)
      .map(([productId, score]) => ({
        ...products[productId],
        id: productId,
        score: score
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3); // Limit to top 3 recommendations

    return recommendations;
  }

  /**
   * Get recommendations based on breed
   * @param {string} breedId - Breed ID
   * @returns {Object} Breed info with product recommendations
   */
  function getBreedRecommendations(breedId) {
    if (!config) return null;

    const breed = config.breeds[breedId];
    if (!breed) return null;

    const recommendations = breed.products.map(productId => ({
      ...config.products[productId],
      id: productId
    }));

    return {
      breed: breed,
      products: recommendations
    };
  }

  /**
   * Get recommendations based on quick assessment
   * @param {string} petType - 'dog' or 'cat'
   * @param {string} concern - Concern ID
   * @param {string} age - 'puppy', 'adult', or 'senior'
   * @returns {Object} Primary product and additional recommendations
   */
  function getQuickRecommendations(petType, concern, age) {
    if (!config) return null;

    const concernData = config.concerns[concern];
    if (!concernData) return null;

    const { products } = config;
    const result = {
      primary: {
        ...products[concernData.primary],
        id: concernData.primary
      },
      secondary: [],
      upsells: []
    };

    // Add secondary products
    if (concernData.secondary) {
      concernData.secondary.forEach(productId => {
        result.secondary.push({
          ...products[productId],
          id: productId
        });
      });
    }

    // Apply age modifiers
    if (concernData.ageModifier && concernData.ageModifier[age]) {
      concernData.ageModifier[age].forEach(productId => {
        result.secondary.push({
          ...products[productId],
          id: productId
        });
      });
    }

    // Add triggered upsells
    if (concernData.triggerUpsells) {
      concernData.triggerUpsells.forEach(productId => {
        result.upsells.push({
          ...products[productId],
          id: productId
        });
      });
    }

    return result;
  }

  /**
   * Get triggered upsell products based on symptoms/selections
   *
   * UPSELL TRIGGERING LOGIC:
   * ========================
   * Upsells are additional products shown after main recommendations.
   * They're triggered when the user's selections match specific patterns.
   *
   * Trigger-based upsells (from product-recommender-data.json):
   * - Paw Balm: triggers on ['itching', 'joint-pain', 'senior', 'skin']
   * - TuDRY: triggers on ['shedding', 'dull-coat', 'skin']
   *
   * The logic uses ANY-match (OR logic):
   * If the user selects ANY symptom in the trigger list, the upsell appears.
   *
   * Example: User selects "Itching"
   * - Paw Balm triggers because 'itching' is in its trigger list
   * - TuDRY does NOT trigger (no match)
   *
   * Example: User selects "Dull Coat" + "Shedding"
   * - TuDRY triggers (matches both, but only needs one)
   * - Paw Balm does NOT trigger
   *
   * @param {string[]} triggers - Array of symptom/concern IDs from user selections
   * @returns {Object[]} Array of upsell products to display
   */
  function getTriggeredUpsells(triggers) {
    if (!config) return [];

    const { upsells, products } = config;
    const triggeredProducts = [];

    // Check each upsell configuration against user's selections
    Object.values(upsells.triggerBased).forEach(upsell => {
      // ANY-match logic: if any user selection matches any trigger, show upsell
      const hasMatch = upsell.triggers.some(t => triggers.includes(t));
      if (hasMatch) {
        triggeredProducts.push({
          ...products[upsell.product],
          id: upsell.product
        });
      }
    });

    return triggeredProducts;
  }

  /**
   * Get universal upsell products (hygiene bundle)
   * @returns {Object} Universal upsell configuration
   */
  function getUniversalUpsells() {
    if (!config) return null;

    const { upsells, products } = config;
    const universal = upsells.universal;

    return {
      title: universal.title,
      bundleHandle: universal.bundleHandle,
      bundleName: universal.bundleName,
      products: universal.products.map(productId => ({
        ...products[productId],
        id: productId
      }))
    };
  }

  /**
   * Get all symptoms for display
   * @returns {Object[]} Array of symptom objects
   */
  function getSymptoms() {
    if (!config) return [];
    return Object.entries(config.symptoms).map(([id, data]) => ({
      id,
      ...data
    }));
  }

  /**
   * Get all breeds for display
   * @returns {Object[]} Array of breed objects
   */
  function getBreeds() {
    if (!config) return [];
    return Object.entries(config.breeds).map(([id, data]) => ({
      id,
      ...data
    }));
  }

  /**
   * Get all concerns for quick assessment
   * @returns {Object[]} Array of concern objects
   */
  function getConcerns() {
    if (!config) return [];
    return Object.entries(config.concerns).map(([id, data]) => ({
      id,
      ...data
    }));
  }

  /**
   * Save quiz data to localStorage
   * Handles QuotaExceededError by clearing old entries and retrying
   * @param {string} quizType - Type of quiz
   * @param {Object} data - Quiz data to save
   */
  function saveQuizData(quizType, data) {
    const key = `recommender_${quizType}`;
    const payload = {
      ...data,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (e) {
      // Handle QuotaExceededError by clearing old recommender data
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        console.warn('[ProductRecommender] Storage quota exceeded, clearing old data');
        // Get all recommender keys, sorted by timestamp (oldest first)
        const recommenderKeys = Object.keys(localStorage)
          .filter(k => k.startsWith('recommender_'))
          .map(k => {
            try {
              const item = JSON.parse(localStorage.getItem(k));
              return { key: k, timestamp: item.timestamp || 0 };
            } catch {
              return { key: k, timestamp: 0 };
            }
          })
          .sort((a, b) => a.timestamp - b.timestamp);

        // Remove oldest entries, keep 3 most recent
        recommenderKeys.slice(0, -3).forEach(item => {
          localStorage.removeItem(item.key);
        });

        // Retry save
        try {
          localStorage.setItem(key, JSON.stringify(payload));
        } catch (retryError) {
          console.error('[ProductRecommender] Failed to save after clearing:', retryError);
        }
      } else {
        console.error('[ProductRecommender] Failed to save quiz data:', e);
      }
    }
  }

  /**
   * Get saved quiz data from localStorage
   * @param {string} quizType - Type of quiz
   * @returns {Object|null} Saved quiz data or null
   */
  function getSavedQuizData(quizType) {
    const key = `recommender_${quizType}`;
    const saved = localStorage.getItem(key);
    if (!saved) return null;

    try {
      const data = JSON.parse(saved);
      // Data expires after 7 days
      if (Date.now() - data.timestamp > SEVEN_DAYS_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }

  /**
   * Submit email capture form
   * Uses RecommenderAPI for Shopify Customer/Discount integration
   * Falls back to FIRST10 discount code if API unavailable
   * @param {Object} formData - Form data
   * @returns {Promise<Object>} Response with discount code
   */
  async function submitEmailCapture(formData) {
    const { email, petName, breed, phone, quizType, selections, recommendations, marketingConsent } = formData;

    // Use RecommenderAPI if available
    if (window.RecommenderAPI) {
      try {
        const result = await window.RecommenderAPI.captureEmail({
          email,
          petName,
          breed,
          phone,
          quizType,
          selections,
          recommendations,
          marketingConsent: marketingConsent !== false
        });

        return {
          success: true,
          discountCode: result.discountCode,
          isFallback: result.isFallback
        };
      } catch (error) {
        console.error('[ProductRecommender] API capture failed, using fallback:', error);
      }
    }

    // Fallback: Use static discount code
    const discountCode = FALLBACK_DISCOUNT_CODE;

    // Save to localStorage
    saveQuizData(quizType, {
      email,
      petName,
      breed,
      selections,
      recommendations: recommendations ? recommendations.map(p => p.id || p) : [],
      discountCode,
      emailCaptured: true
    });

    // Track event
    trackEvent(EVENTS.EMAIL_CAPTURED, {
      quiz_type: quizType,
      has_phone: !!phone,
      has_pet_name: !!petName,
      has_breed: !!breed,
      is_fallback: true
    });

    return {
      success: true,
      discountCode: discountCode,
      isFallback: true
    };
  }

  /**
   * Track analytics event
   * @param {string} eventName - Event name
   * @param {Object} data - Event data
   */
  function trackEvent(eventName, data = {}) {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag('event', eventName, {
        event_category: 'recommender',
        ...data
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      window.fbq('trackCustom', eventName, data);
    }

    // Google Tag Manager dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...data
      });
    }

    // Debug logging
    console.log('[Recommender]', eventName, data);
  }

  /**
   * Add product to cart
   * @param {string} variantId - Shopify variant ID
   * @param {number} quantity - Quantity to add
   * @param {string} source - Source quiz type
   * @returns {Promise<Object>} Cart response
   */
  async function addToCart(variantId, quantity = 1, source = 'recommender') {
    // Validate variantId
    if (!variantId) {
      console.error('[ProductRecommender] Variant ID is required');
      return null;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: {
            _source: source
          }
        })
      });

      // Check for API errors (422 = out of stock, 404 = variant not found)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || `Cart API error: ${response.status}`);
      }

      const result = await response.json();

      // Track event
      trackEvent(EVENTS.PRODUCT_ADDED, {
        product_id: variantId,
        quiz_source: source
      });

      // Trigger cart update event for theme
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Open Shopflo cart drawer if available
      if (typeof window.UWCartBtn === 'function') {
        window.UWCartBtn(); // waits for the Shopflo bundle if it has not loaded yet
      } else if (typeof window.handleFloCartBtn === 'function') {
        window.handleFloCartBtn();
      }

      return result;
    } catch (error) {
      console.error('[ProductRecommender] Failed to add to cart:', error);
      throw error; // Re-throw so callers can handle the error
    }
  }

  /**
   * Get product by handle from Shopify
   * @param {string} handle - Product handle
   * @returns {Promise<Object>} Product data
   */
  async function getProduct(handle) {
    if (!handle) {
      console.error('[ProductRecommender] Product handle is required');
      return null;
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`Product not found: ${handle}`);
      }
      return await response.json();
    } catch (error) {
      console.error('[ProductRecommender] Failed to fetch product:', error);
      return null;
    }
  }

  // Public API
  return {
    init,
    getSymptomRecommendations,
    getBreedRecommendations,
    getQuickRecommendations,
    getTriggeredUpsells,
    getUniversalUpsells,
    getSymptoms,
    getBreeds,
    getConcerns,
    saveQuizData,
    getSavedQuizData,
    submitEmailCapture,
    trackEvent,
    addToCart,
    getProduct
  };
})();
